<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'

import { type UseMouseEventExtractor, useMouse, useParentElement } from '@vueuse/core'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'

const props = defineProps<{
    element: any
    elementIndex: number
    isNew?: boolean
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const afterImage = computed(() => props.element.frameContent?.afterPicture)
const beforeImage = computed(() => props.element.frameContent?.beforePicture)
const selectorPosition = ref(0)
const editorBeforeAfter: any = useParentElement()
const extractor: UseMouseEventExtractor = event => event instanceof Touch ? null : [event.offsetX, event.offsetY]
const mouseWithExtractor = reactive(useMouse({ target: editorBeforeAfter, type: extractor }))

const setPoint = () => {
    if (!editorBeforeAfter.value) return
    if (editorBeforeAfter.value.offsetWidth - 20 < mouseWithExtractor.x) return
    if (mouseWithExtractor.x < 20) return

    selectorPosition.value = mouseWithExtractor.x
}

const point = computed(() => selectorPosition.value < 0 ? 0 : selectorPosition.value)

onMounted(() => {
    selectorPosition.value = editorBeforeAfter.value?.offsetWidth
        ? editorBeforeAfter.value.offsetWidth / 2
        : 0
})
</script>

<template>
    <div
        tabindex="0"
        :id="'betaEditorBeforeAfter-' + elementIndex"
        ref="editorBeforeAfter"
        :data-active="isActive"
        class="editor-before-after-beta"
        :schema="JSON.stringify(schema)"
        @drag="setPoint"
        @click="setPoint"
    >
        <beta-base-image
            :style="{ clip: `rect(auto, ${point}px, auto, auto)` }"
            class-name="pointer-events-none absolute inset-0 z-10 block h-full w-full select-none object-cover"
            :src="afterImage"
            :external-image="afterImage === 'https://uli-pictures.s3-sa-east-1.amazonaws.com/panel/before-after-mockup-2.jpg'"
        />

        <beta-base-image
            class-name="pointer-events-none block h-full w-full select-none object-cover"
            :src="beforeImage"
            :external-image="beforeImage === 'https://uli-pictures.s3-sa-east-1.amazonaws.com/panel/before-after-mockup-1.jpg'"
        />

        <div
            class="absolute top-0 z-20 h-full w-10"
            :style="{ left: `${point ? point - 20 : 0}px` }"
            @click.stop.prevent
        >
            <div class="relative h-full w-full cursor-col-resize">
                <div class="absolute left-1/2 top-0 h-[44%] w-1 -translate-x-1/2 bg-white drop-shadow-sm md:h-[46%]" />
                <div class="absolute left-1/2 top-1/2 h-[12%] w-[64px] -translate-x-1/2 -translate-y-1/2 drop-shadow-sm md:h-[10%]">
                    <svg class="h-full w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 68" fill="none">
                        <path d="M24 42L16 34L24 26M44 42L52 34L44 26M66 34C66 51.6731 51.6731 66 34 66C16.3269 66 2 51.6731 2 34C2 16.3269 16.3269 2 34 2C51.6731 2 66 16.3269 66 34Z" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <div class="absolute bottom-0 left-1/2 h-[44%] w-1 -translate-x-1/2 bg-white drop-shadow-sm md:h-[46%]" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.editor-before-after-beta {
    @apply relative h-auto cursor-col-resize select-none border-2 border-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-before-after-beta[data-active='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}
</style>
